import React, { Fragment, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Card, CardBody, Row, Col, CardHeader, Form, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Breadcrumbs, H3, H4 } from '../../AbstractElements';
import Button from '../Common/Elements/Button';
import { Filter, Search, Status } from '../../Constant';
import SweetAlert from 'sweetalert2';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../Utils/restUtils';
import { getLeaveRequest, getLeaveRequestRole, changeLeaveRequestStatus, changeLeaveRequestStatusRole } from '../../CommonUrl';
import Transparentspinner from '../Common/Loader/Transparentspinner';
import { wrap } from 'lodash';

const LeaveMangement = () => {
    const navigate = useNavigate();
    const authState = useSelector((state) => state.auth);
    const { register, handleSubmit } = useForm();

    const [loading, setLoading] = useState(false);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortConfig, setSortConfig] = useState({ column: '', order: '' });
    const [filters, setFilters] = useState({ search: '', status: '' });

    const toggleStyle = { padding: '10px 20px', opacity: '0.8', fontSize: '14px' };


    const fetchLeaves = async (page, size = perPage, search = '', status = '', sortBy = '', sortOrder = '') => {
        setLoading(true);
        try {
            const endpoint = authState.userRole === 1 ? getLeaveRequest : getLeaveRequestRole;
            const response = await getClientUrlDataToken(
                authState.apiUrl,
                `${endpoint}?page=${page}&page_length=${size}&search=${search}&status=${status}&sort_by=${sortBy}&sort_order=${sortOrder}`
            );

            if (response.status === true) {
                const SerialNo = (page - 1) * size + 1;



                const transformedData = response.data.data.map((row, index) => {


                    return {
                        id: row.id,
                        Si: SerialNo + index,
                        name: row?.employee_name || 'N/A',
                        leaveType: row?.leave_type || 'N/A',
                        reason: row?.reason || 'N/A',
                        from: row?.from_date || 'N/A',
                        to: row?.to_date || 'N/A',
                        days: row?.total_days || 'N/A',
                        applied: row?.created_at || 'N/A',
                        status: row?.status || 'N/A',

                    };
                });

                setLeaveRequests(transformedData);
                setTotalRows(response.data.total || 0);
            } else {
                toast.error(response.data?.message || "Failed to fetch leave requests");
            }
        } catch (error) {
            console.error("Error fetching leave requests:", error);
            toast.error("An error occurred while fetching leave requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeaves(1);
    }, [authState.apiUrl, authState.userRole]);



    const handleSort = (column) => {
        let newOrder = 'asc';
        if (sortConfig.column === column.id) {
            newOrder = sortConfig.order === 'asc' ? 'desc' : 'asc';
        }
        setSortConfig({ column: column.id, order: newOrder });
        fetchLeaves(currentPage, perPage, filters.search, filters.status, column.id, newOrder);
    };

    const handlePageChange = page => {
        fetchLeaves(page, perPage, filters.search, filters.status, sortConfig.column, sortConfig.order);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        fetchLeaves(page, newPerPage, filters.search, filters.status, sortConfig.column, sortConfig.order);
        setPerPage(newPerPage);
    };

    const onSubmit = async (data) => {
        const search = data.search;
        const status = data.status;
        setFilters({
            search,
            status
        });
        fetchLeaves(1, perPage, search, status, sortConfig.column, sortConfig.order);
        setCurrentPage(1);
    };

    const pendingCount = leaveRequests.filter(r => String(r.status).toLowerCase() === 'pending').length;
    const approvedCount = leaveRequests.filter(r => String(r.status).toLowerCase() === 'approved').length;

    // Top stat cards data
    const stats = [
        { title: 'Pending Requests', value: String(pendingCount), valueColor: '#d97706', subtext: 'Pending requests in page', iconBg: '#fef3c7' },
        { title: 'Approved This Month', value: String(approvedCount), valueColor: '#059669', subtext: 'Approved requests in page', iconBg: '#ecfdf5' },
        { title: 'Leave Days Used (May)', value: '47', valueColor: '#111827', subtext: 'Across all employees', iconBg: '#eff6ff' },
    ];

    const ActionCell = ({ row }) => {
        const [dropdownOpen, setDropdownOpen] = React.useState(false);
        const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

        const handleStatusChange = (row, newStatus) => {
            SweetAlert.fire({
                title: `Confirm ${newStatus.toLowerCase()}?`,
                text: `Are you sure you want to mark this request as ${newStatus.toLowerCase()}?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then(async (result) => {
                if (result.value) {
                    setLoading(true);
                    const payload = {
                        leave_request_id: row.id,
                        status: newStatus
                    };
                    const endpoint = authState.userRole === 1 ? changeLeaveRequestStatus : changeLeaveRequestStatusRole;
                    try {
                        const response = await postClientUrlWithToken(
                            authState.apiUrl,
                            endpoint,
                            payload
                        );
                        if (response.status === 200) {
                            if (response.data.status === true) {
                                SweetAlert.fire('Success!', response.data.message || `Leave request ${newStatus.toLowerCase()} successfully`, 'success');
                                fetchLeaves(currentPage, perPage, filters.search, filters.status, sortConfig.column, sortConfig.order);
                            } else {
                                toast.error(response.data?.message || "Failed to update status");
                            }
                        } else {
                            toast.error("Failed to update status");
                        }
                    } catch (error) {
                        console.error("Error updating status:", error);
                        toast.error("An error occurred while updating status");
                    } finally {
                        setLoading(false);
                    }
                }
            });
        };

        return (
            <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown}>
                <DropdownToggle color="#00000" style={{ fontSize: '14px', zIndex: 1000 }}>
                    <i className="fa fa-ellipsis-v"></i>
                </DropdownToggle>
                <DropdownMenu container="body">
                    {row.status === "pending" && <>
                        <DropdownItem style={toggleStyle} onClick={() => handleStatusChange(row, 'approved')}>Approve</DropdownItem>
                        <DropdownItem style={{ padding: '10px 20px', color: 'red', opacity: '0.8', fontSize: '14px', margin: '0' }} onClick={() => handleStatusChange(row, 'rejected')}>Reject</DropdownItem>
                    </>
                    }
                    {row.status === "approved" && <>
                        <DropdownItem style={toggleStyle} onClick={() => handleStatusChange(row, 'pending')}>Pending</DropdownItem>
                        <DropdownItem style={{ padding: '10px 20px', color: 'red', opacity: '0.8', fontSize: '14px', margin: '0' }} onClick={() => handleStatusChange(row, 'rejected')}>Reject</DropdownItem>
                    </>
                    }
                    {row.status === "rejected" && <>
                        <DropdownItem style={toggleStyle} onClick={() => handleStatusChange(row, 'pending')}>Pending</DropdownItem>
                        <DropdownItem style={toggleStyle} onClick={() => handleStatusChange(row, 'approved')}>Approve</DropdownItem>
                    </>
                    }
                </DropdownMenu>
            </Dropdown>
        );
    };

    const columns = [
        {
            name: <H4>Sl.No</H4>,
            selector: row => row.Si,
            sortable: true,
            width: '80px',

        },
        {
            name: <i className="fa fa-gears" style={{ fontSize: '15px' }}></i>,
            center: true,
            cell: row =>
                <ActionCell row={row} />,
            width: '80px',


        },
        {
            name: <H4>Staff</H4>,
            selector: row => row.name,
            sortable: true,
            minWidth: '140px',

        },
        {
            name: <H4>Leave type</H4>,
            selector: row => row.leaveType,
            sortable: true,

            minWidth: '120px'
        },
        {
            name: <H4>Status</H4>,
            selector: row => row.status,
            sortable: true,
            cell: row => (
                <span className={`${row.status === 'pending' ? 'badge-draft' : `badge-${row.status}`}`}  >
                    {row.status}
                </span>
            ),
            minWidth: '120px'
        },
        {
            name: <H4>From</H4>,
            selector: row => row.from,
            sortable: true,
            minWidth: '160px',
            wrap: true

        },
        {
            name: <H4>To</H4>,
            selector: row => row.to,
            sortable: true,
            minWidth: '160px',
            wrap: true
        },
        {
            name: <H4>Days</H4>,
            selector: row => row.days,
            sortable: true,
        },
        {
            name: <H4>Reason</H4>,
            selector: row => row.reason,
            minWidth: '200px',
        },
        {
            name: <H4>Applied</H4>,
            selector: row => row.applied,
            sortable: true,
            minWidth: '160px',
            wrap: true
        },


    ];

    return (
        <Fragment>
            {loading && <Transparentspinner />}
            <Breadcrumbs mainTitle={'Leave Management'} parent="Attendance" title={'Leave Management'} />

            <div className="container-fluid">
                {/* Top Stat Cards */}
                <Row className="mb-4">
                    {stats.map((stat, idx) => (
                        <Col key={idx} md={4}>
                            <Card className="shadow-sm h-100" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                                <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-start">
                                        <div>
                                            <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '600' }}>{stat.title}</span>
                                            <h3 style={{ color: stat.valueColor, fontWeight: '700', fontSize: '28px', margin: '8px 0', fontFamily: 'Inter, sans-serif' }}>{stat.value}</h3>
                                            <span style={{ color: '#6b7280', fontSize: '12px' }}>{stat.subtext}</span>
                                        </div>
                                        <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: stat.iconBg }}></div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* Main Content with Table and Filters */}
                <Card className="shadow-sm" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    {/* Filter Section */}
                    <CardHeader>
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                            <H3>Leave Requests</H3>

                            <Button
                                className="btn btn-primary"
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/add-leave`)}
                            >
                                Add Leave
                            </Button>
                        </div>

                    </CardHeader>
                    <CardBody className="py-3 px-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <Form className="needs-validation" noValidate onSubmit={handleSubmit(onSubmit)}>
                            <Row className="ps-3 gap-sm-3 gap-md-2 mb-4 filterGap">
                                <Col sm="12" md='5' lg='2'>
                                    <input
                                        className="form-control"
                                        placeholder={Search}
                                        type="text"
                                        {...register('search')}
                                    />
                                </Col>
                                <Col sm="12" md="5" lg='2' className="form-element">
                                    <select className="form-select" {...register('status')}>
                                        <option value="">{Status}</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </Col>
                                <Col sm="12" md="5" lg='1' className="custom-filtrs">
                                    <Button attrBtn={{ color: "primary" }} type="submit">{Filter}</Button>
                                </Col>
                            </Row>
                        </Form>
                        <div>
                            <DataTable
                                columns={columns}
                                data={leaveRequests}
                                onSort={handleSort}
                                sortServer
                                pagination
                                paginationServer
                                defaultSortAsc
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                paginationTotalRows={totalRows}
                                paginationDefaultPage={currentPage}
                                onChangeRowsPerPage={handlePerRowsChange}
                                onChangePage={handlePageChange}
                                fixedHeader
                            />
                        </div>
                    </CardBody>
                </Card>
            </div>
        </Fragment>
    );
};

export default LeaveMangement;